import debounce from 'lodash/debounce'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter } from 'types/common/query'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { searchGlobal } from 'API/search'
import { mapTitleToValueSearchOrder } from 'components/Pages/SearchPage/utils'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { IAsyncSelectOption } from 'interfaces/common'
import { getValidArray } from '../utils/common'

export interface GlobalSearchDto {
  searchText: string
  isPublished?: boolean
  groupIds?: number[]
  tagIds?: number[]
  processIds?: number[]
  /** Each element is an OrgMembership.id (membershipId), not a User.id */
  creatorIds?: number[]
  collectionName: EEsGlobalSearchCollection | ''
  userId: number
}

export interface ISearchFilterForm {
  collections?: IAsyncSelectOption<number>[]
  groups?: IAsyncSelectOption<number>[]
  sort?: string
  tags?: IAsyncSelectOption<number>[]
  creators?: IAsyncSelectOption<number>[]
}

export default class SearchStore {
  rootStore: RootStore

  isSearching: boolean | null = null
  isManageMode = false
  searchKeyword: string = ''
  searchResults: IEsGlobalSearchItem[] = []
  displayTypes: EEsGlobalSearchCollection[] = [EEsGlobalSearchCollection.PROCESS]
  totalResults: number = 0
  searchText?: string = undefined

  // Serialized last-search key (filter + active org). The org must be part of the
  // key: orgId is stamped by the axios interceptor, not the filter, so switching
  // teams would otherwise produce an identical filter and skip the refetch.
  cacheKey: string = ''

  searchFilter: ISearchFilterForm = {
    collections: [],
    groups: [],
    sort: '',
    tags: [],
    creators: []
  }

  groupIds: number[] = []
  tagIds: number[] = []
  processIds: number[] = []
  size: number = 40
  from: number = 0

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  public countFilter(): number {
    const hasSort = this.searchFilter?.sort ? 1 : 0
    const numGroups = getValidArray(this.searchFilter?.groups).length > 0 ? 1 : 0
    const numTags = getValidArray(this.searchFilter?.tags).length > 0 ? 1 : 0
    return hasSort + numGroups + numTags
  }

  public setDisplayTypes = (newDisplayTypes: EEsGlobalSearchCollection[]): void => {
    if (newDisplayTypes.length === 0) {
      this.displayTypes = [EEsGlobalSearchCollection.PROCESS, EEsGlobalSearchCollection.COLLECTION]
      return
    }
    this.displayTypes = newDisplayTypes || [EEsGlobalSearchCollection.PROCESS, EEsGlobalSearchCollection.COLLECTION]
  }

  public setSearchText = (searchText: string): void => {
    this.searchText = searchText
  }

  public setManageMode = (isManageMode: boolean): void => {
    this.isManageMode = isManageMode
  }

  public async search(): Promise<void> {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
    try {
      this.isSearching = true
      this.searchKeyword = this.searchText ?? ''

      const filter: IFilter<GlobalSearchDto> = {
        where: {
          searchText: this?.searchText ?? '',
          isPublished: true,
          groupIds: this.searchFilter.groups?.map(group => Number(group.value)) || [],
          tagIds: this.searchFilter.tags?.map(tag => Number(tag.value)) || [],
          creatorIds: this.searchFilter.creators?.map(creator => Number(creator.value)) || [],
          processIds: this.processIds.length > 0 ? this.processIds : undefined,
          collectionName: getValidArray(this.displayTypes).length === 1 ? this.displayTypes[0] : ''
        },
        skip: this.from,
        limit: this.size,
        order: [mapTitleToValueSearchOrder(`${this.searchFilter?.sort}`)]
      }

      if (!this.searchFilter?.sort || this?.searchFilter?.sort === 'Most relevant') {
        delete filter.order
      }

      if (!this.searchFilter?.groups?.length && filter?.where?.groupIds) {
        delete filter.where.groupIds
      }

      if (!this.searchFilter?.tags?.length && filter?.where?.tagIds) {
        delete filter.where.tagIds
      }

      if (!this.searchFilter?.creators?.length && filter?.where?.creatorIds) {
        delete filter.where.creatorIds
      }

      if (!this.processIds?.length && filter?.where?.processIds) {
        delete filter.where.processIds
      }

      const cacheKey = JSON.stringify({ orgId: this.rootStore.authStore.activeOrgId, filter })
      if (this.cacheKey === cacheKey && this.searchResults.length > 0) {
        // Cache hit — no fetch. Reset the loading flag the try-block just set; the early
        // return skips the finally, so without this consumers (e.g. Collection Detail view
        // after returning from edit) stay stuck on the "Searching…" state forever.
        this.isSearching = false
        return
      }
      this.cacheKey = cacheKey

      const searchResult = await searchGlobal(filter)
      this.searchResults = searchResult.data
      this.totalResults = searchResult.total
    } catch (error: any) {
      console.log('🚀 Log ~ file: searchStore.ts Log ~ line 103 Log ~ SearchStore Log ~ search Log ~ error', error)
    } finally {
      this.isSearching = false
    }
  }

  public changePage(page: number): void {
    this.from = (page - 1) * this.size
    this.search()
  }

  public changeSize = (pageIndex: number = 10): void => {
    this.size = Number(pageIndex)
  }

  debouncedSearch = debounce(this.search, 700)

  public changeSearchText(searchText: string): void {
    if (searchText !== 'false') {
      this.isSearching = true
      this.searchText = searchText
      this.debouncedSearch()
    }
  }

  public changeFilterTags(tags: IAsyncSelectOption<number>[]): void {
    this.searchFilter = { ...this.searchFilter, tags }
  }

  public changeGroupIds(groupIds: number[]): void {
    this.groupIds = groupIds
  }

  public changeTagIds(tagIds: number[]): void {
    this.tagIds = tagIds
  }

  public changeProcessIds(processIds: number[]): void {
    this.processIds = processIds
  }

  public changeSearchFilter(searchFilter: ISearchFilterForm): void {
    this.searchFilter = searchFilter
  }
  public clearSearchResults(): void {
    this.searchResults = []
    this.isSearching = false
  }
  public clearSearchFilter(): void {
    this.searchFilter = {
      collections: [],
      groups: [],
      sort: '',
      tags: [],
      creators: []
    }
    this.groupIds = []
    this.tagIds = []
    this.processIds = []
  }
}
